import { defineMessages } from 'react-intl';
import type { MessageTag, NoMessageValues } from 'react-intl';

export const messages = defineMessages<{
  readonly title: NoMessageValues;
  readonly loadError: NoMessageValues;
  readonly invoiceNotFoundError: NoMessageValues;
  readonly dateLabel: NoMessageValues;
  readonly invoiceNumberLabel: NoMessageValues;
  readonly customerNumberLabel: NoMessageValues;
  readonly paymentMethodLabel: NoMessageValues;
  readonly deliveryAddressLabel: NoMessageValues;
  readonly invoiceAddressLabel: NoMessageValues;
  readonly articleNumberLabel: NoMessageValues;
  readonly quantityLabel: NoMessageValues;
  readonly productReturned: NoMessageValues;
  readonly reportProblemButton: NoMessageValues;
  readonly articleHeader: NoMessageValues;
  readonly invoiceHeader: NoMessageValues;
  readonly itemValueLabel: NoMessageValues;
  readonly priceModificationFallbackLabel: NoMessageValues;
  readonly bookedServices: NoMessageValues;
  readonly endPriceLabel: NoMessageValues;
  readonly subtotalLabel: NoMessageValues;
  readonly grandTotalLabel: NoMessageValues;
  readonly paymentPlanLabel: NoMessageValues;
  readonly originalRatePrefix: NoMessageValues;
  readonly originalRateText: { readonly link: MessageTag };
  readonly linkHere: NoMessageValues;
  readonly payWithinNotice: NoMessageValues;
  readonly depositNotice: NoMessageValues;
  readonly bankDetailsTitle: NoMessageValues;
  readonly bankRecipientLabel: NoMessageValues;
  readonly bicLabel: NoMessageValues;
  readonly ibanLabel: NoMessageValues;
  readonly purposeLabel: NoMessageValues;
  readonly purposeValue: NoMessageValues;
  readonly companyAddressVATInfo: NoMessageValues;
  readonly termsText: { readonly link: MessageTag };
  readonly ownershipNote: NoMessageValues;
  readonly downloadButton: NoMessageValues;
  readonly showAvailableLink: NoMessageValues;
  readonly reimbursementsTitle: NoMessageValues;
  readonly reimbursementDateLabel: NoMessageValues;
  readonly reimbursementAmountLabel: NoMessageValues;
  readonly reimbursementsSumLabel: NoMessageValues;
  readonly reimbursementsOpenLabel: NoMessageValues;
  readonly reimbursementWithoutArticlePrefix: NoMessageValues;
}>({
  title: {
    id: 'InvoiceDetail.Title',
    defaultMessage: 'Rechnung',
  },
  loadError: {
    id: 'InvoiceDetail.LoadError',
    defaultMessage: 'Beim Laden der Rechnung ist ein Fehler aufgetreten.',
  },
  invoiceNotFoundError: {
    id: 'InvoiceDetail.InvoiceNotFoundError',
    defaultMessage:
      'Die angeforderte Rechnung konnte in diesem Konto nicht gefunden werden. Bitte stelle sicher, dass du mit dem Konto angemeldet bist, dem die Rechnung zugeordnet ist.',
  },
  dateLabel: {
    id: 'InvoiceDetail.DateLabel',
    defaultMessage: 'Rechnungsdatum:',
  },
  invoiceNumberLabel: {
    id: 'InvoiceDetail.InvoiceNumberLabel',
    defaultMessage: 'Rechnungsnummer:',
  },
  customerNumberLabel: {
    id: 'InvoiceDetail.CustomerNumberLabel',
    defaultMessage: 'Kundennummer:',
  },
  paymentMethodLabel: {
    id: 'InvoiceDetail.PaymentMethodLabel',
    defaultMessage: 'Zahlungsart:',
  },
  deliveryAddressLabel: {
    id: 'InvoiceDetail.DeliveryAddressLabel',
    defaultMessage: 'Lieferadresse:',
  },
  invoiceAddressLabel: {
    id: 'InvoiceDetail.InvoiceAddressLabel',
    defaultMessage: 'Rechnungsadresse:',
  },
  articleNumberLabel: {
    id: 'InvoiceDetail.ArticleNumberLabel',
    defaultMessage: 'Art.Nr.:',
  },
  quantityLabel: {
    id: 'InvoiceDetail.QuantityLabel',
    defaultMessage: 'Menge:',
  },
  productReturned: {
    id: 'InvoiceDetail.ProductReturned',
    defaultMessage: 'Produkt wurde zurückgeschickt',
  },
  reportProblemButton: {
    id: 'InvoiceDetail.ReportProblemButton',
    defaultMessage: 'Ein Problem mit diesem Artikel melden',
  },
  articleHeader: {
    id: 'InvoiceDetail.ArticleHeader',
    defaultMessage: 'Artikel',
  },
  invoiceHeader: {
    id: 'InvoiceDetail.InvoiceHeader',
    defaultMessage: 'Rechnung',
  },
  itemValueLabel: {
    id: 'InvoiceDetail.ItemValueLabel',
    defaultMessage: 'Warenwert:',
  },
  priceModificationFallbackLabel: {
    id: 'InvoiceDetail.PriceModificationFallbackLabel',
    defaultMessage: 'Preisänderung:',
  },
  bookedServices: {
    id: 'InvoiceDetail.BookedServices',
    defaultMessage: 'Gebuchte Services',
  },
  endPriceLabel: {
    id: 'InvoiceDetail.EndPriceLabel',
    defaultMessage: 'Endpreis:',
  },
  subtotalLabel: {
    id: 'InvoiceDetail.SubtotalLabel',
    defaultMessage: 'Zwischensumme:',
  },
  grandTotalLabel: {
    id: 'InvoiceDetail.GrandTotalLabel',
    defaultMessage: 'Gesamtsumme:',
  },
  paymentPlanLabel: {
    id: 'InvoiceDetail.PaymentPlanLabel',
    defaultMessage: 'Zahlungsplan',
  },
  originalRatePrefix: {
    id: 'InvoiceDetail.OriginalRatePrefix',
    defaultMessage:
      'Dies ist deine ursprüngliche Rate. Die aktuellen Raten inkl. der Zusammensetzung findest du',
  },
  originalRateText: {
    id: 'InvoiceDetail.OriginalRateText',
    defaultMessage:
      'Dies ist deine ursprüngliche Rate. Die aktuellen Raten inkl. der Zusammensetzung findest du <link>hier</link>.',
  },
  linkHere: {
    id: 'InvoiceDetail.LinkHere',
    defaultMessage: 'hier',
  },
  payWithinNotice: {
    id: 'InvoiceDetail.PayWithinNotice',
    defaultMessage:
      'Bitte zahlen Sie binnen 14 Tagen den gesamten Rechnungsbetrag oder bei Teilzahlungswunsch nur 10 EUR.',
  },
  depositNotice: {
    id: 'InvoiceDetail.DepositNotice',
    defaultMessage: 'Eine evtl. bereits geleistete Anzahlung bitte vom Rechnungsbetrag abziehen.',
  },
  bankDetailsTitle: {
    id: 'InvoiceDetail.BankDetailsTitle',
    defaultMessage: 'Unsere Bankverbindung',
  },
  bankRecipientLabel: {
    id: 'InvoiceDetail.BankRecipientLabel',
    defaultMessage: 'Empfänger:',
  },
  bicLabel: {
    id: 'InvoiceDetail.BICLabel',
    defaultMessage: 'BIC:',
  },
  ibanLabel: {
    id: 'InvoiceDetail.IBANLabel',
    defaultMessage: 'IBAN:',
  },
  purposeLabel: {
    id: 'InvoiceDetail.PurposeLabel',
    defaultMessage: 'Verwendungszweck:',
  },
  purposeValue: {
    id: 'InvoiceDetail.PurposeValue',
    defaultMessage: '[Kundennummer - Name Nachname]',
  },
  companyAddressVATInfo: {
    id: 'InvoiceDetail.companyAddressVATInfo',
    defaultMessage: 'Hier pro Marke eintragen',
  },
  termsText: {
    id: 'InvoiceDetail.TermsText',
    defaultMessage:
      'Bitte beachten Sie unsere Geschäftsbedingungen unter <link>https://www.quelle.de/service-hilfe/ueber-uns/agb/</link>.',
  },
  ownershipNote: {
    id: 'InvoiceDetail.OwnershipNote',
    defaultMessage: 'Die Ware bleibt bis zur vollständigen Bezahlung unser Eigentum.',
  },
  downloadButton: {
    id: 'InvoiceDetail.DownloadButton',
    defaultMessage: 'Herunterladen und Drucken',
  },
  showAvailableLink: {
    id: 'InvoiceDetail.ShowAvailableLink',
    defaultMessage: 'Rechnung anzeigen',
  },
  reimbursementsTitle: {
    id: 'InvoiceDetail.ReimbursementsTitle',
    defaultMessage: 'Gutschriften',
  },
  reimbursementDateLabel: {
    id: 'InvoiceDetail.ReimbursementDateLabel',
    defaultMessage: 'gutgeschrieben am:',
  },
  reimbursementAmountLabel: {
    id: 'InvoiceDetail.ReimbursementAmountLabel',
    defaultMessage: 'Gutschrift:',
  },
  reimbursementsSumLabel: {
    id: 'InvoiceDetail.ReimbursementsSumLabel',
    defaultMessage: 'Summe:',
  },
  reimbursementsOpenLabel: {
    id: 'InvoiceDetail.ReimbursementsOpenLabel',
    defaultMessage: 'Offen:',
  },
  reimbursementWithoutArticlePrefix: {
    id: 'InvoiceDetail.ReimbursementWithoutArticlePrefix',
    defaultMessage: 'Gutschrift ohne Artikelbezug',
  },
});
